import { useTranslation } from 'react-i18next';
import { Users, Building2, Trophy, Medal } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import { useStats } from '@/hooks/useApi';

const CHART_COLORS = ['#2d6a4f', '#b91c1c', '#0369a1', '#d4a843', '#8b6f47', '#0f766e', '#7c3aed', '#ec4899', '#f59e0b'];

export default function StatsPage() {
  const { t } = useTranslation();
  const { data: stats, isLoading } = useStats();

  const summaryCards = [
    { label: t('stats.totalParticipants'), value: stats?.totalParticipants ?? '--', icon: Users },
    { label: t('stats.totalTownships'), value: stats?.totalTownships ?? '--', icon: Building2 },
    { label: t('stats.totalCompetitions'), value: stats?.totalCompetitions ?? '--', icon: Trophy },
    { label: t('stats.totalMedals'), value: stats?.totalMedals ?? '--', icon: Medal },
  ];

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="section-title">{t('stats.title')}</h1>
        <p className="section-subtitle">{t('stats.subtitle')}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card animate-pulse h-24" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card animate-pulse h-80" />
          ))}
        </div>
      </div>
    );
  }

  // Prepare medal pie data
  const medalPieData = stats?.medalDistribution?.flatMap((m) => [
    { name: `${m.township} ${t('scores.gold')}`, value: m.gold },
    { name: `${m.township} ${t('scores.silver')}`, value: m.silver },
    { name: `${m.township} ${t('scores.bronze')}`, value: m.bronze },
  ]).filter((d) => d.value > 0) ?? [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <h1 className="section-title">{t('stats.title')}</h1>
      <p className="section-subtitle">{t('stats.subtitle')}</p>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        {summaryCards.map((card) => (
          <div key={card.label} className="card text-center">
            <card.icon className="w-8 h-8 text-primary mx-auto mb-2" />
            <div className="text-3xl font-bold text-primary tabular-nums">{card.value}</div>
            <div className="text-sm text-text-secondary mt-1">{card.label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Participation by Township */}
        <div className="card">
          <h3 className="font-bold text-lg mb-6">{t('stats.participationByTownship')}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats?.participationByTownship ?? []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="name" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                }}
              />
              <Bar dataKey="count" fill="var(--primary)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Participation by Competition */}
        <div className="card">
          <h3 className="font-bold text-lg mb-6">{t('stats.participationByCompetition')}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats?.participationByCompetition ?? []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis type="number" fontSize={12} />
              <YAxis type="category" dataKey="name" fontSize={11} width={80} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                }}
              />
              <Bar dataKey="count" fill="var(--secondary)" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Medal Distribution */}
        <div className="card">
          <h3 className="font-bold text-lg mb-6">{t('stats.medalDistribution')}</h3>
          {stats?.medalDistribution && stats.medalDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.medalDistribution}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="township" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                  }}
                />
                <Legend />
                <Bar dataKey="gold" name={t('scores.gold')} fill="#EAB308" stackId="medals" />
                <Bar dataKey="silver" name={t('scores.silver')} fill="#9CA3AF" stackId="medals" />
                <Bar dataKey="bronze" name={t('scores.bronze')} fill="#B45309" stackId="medals" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={medalPieData.length > 0 ? medalPieData : [{ name: 'N/A', value: 1 }]}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {(medalPieData.length > 0 ? medalPieData : [{ name: 'N/A', value: 1 }]).map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Registration Trend */}
        <div className="card">
          <h3 className="font-bold text-lg mb-6">{t('stats.registrationTrend')}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats?.registrationTrend ?? []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="var(--primary)"
                strokeWidth={2}
                dot={{ r: 4, fill: 'var(--primary)' }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
